import React, { useState } from 'react';
import NativeCashfree from '../services/native/cashfree';
import { API_URL } from '../config/api';

const PrepaidActivationPortal = ({
  user,
  subscriptionData,
  onLogout,
  onActivationSuccess,
  showMessage
}) => {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const token = localStorage.getItem('token');
  const planName = user?.plan_name || subscriptionData?.plan_name || 'Professional Plan';
  const planPrice = user?.plan_price || subscriptionData?.plan_price || '₹999/mo';
  const planBillingCycle = user?.plan_billing_cycle || subscriptionData?.plan_billing_cycle || 'monthly';
  const userLimit = user?.user_limit || subscriptionData?.user_limit || 15;
  const assetLimit = user?.asset_limit || subscriptionData?.asset_limit || 100;
  const clientCode = user?.client_code || user?.code || 'TENANT';
  const clientName = user?.client_name || user?.organization_name || 'Your Organization';
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const handlePayAndActivate = async () => {
    setIsCheckingOut(true);
    setCheckoutError('');

    try {
      if (showMessage) showMessage(`Initializing Cashfree checkout for ${planName}...`, 'info');

      // 1. Create order on backend
      const response = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_name: planName,
          billing_cycle: planBillingCycle
        })
      });

      const orderData = await response.json();
      if (!response.ok || !orderData.payment_session_id) {
        throw new Error(orderData.detail || 'Failed to generate payment session');
      }

      // 2. Open Cashfree Checkout Modal
      const checkoutResult = await NativeCashfree.checkout({
        paymentSessionId: orderData.payment_session_id,
        orderId: orderData.order_id,
        environment: 'PRODUCTION'
      });

      // 3. Verify payment on backend
      if (checkoutResult && (checkoutResult.status === 'SUCCESS' || checkoutResult.orderId)) {
        if (showMessage) showMessage('Verifying payment and activating workspace...', 'info');

        const verifyRes = await fetch(`${API_URL}/api/subscription/verify-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ order_id: orderData.order_id })
        });

        const verifyData = await verifyRes.json();
        if (verifyRes.ok && verifyData.success) {
          if (showMessage) showMessage(verifyData.message || 'Workspace activated successfully!', 'success');
          if (onActivationSuccess) {
            onActivationSuccess();
          } else {
            window.location.reload();
          }
        } else {
          throw new Error(verifyData.detail || 'Payment verification pending. Please refresh in a moment.');
        }
      }
    } catch (err) {
      console.error('Prepaid activation checkout error:', err);
      const errMsg = err.message || 'Payment was cancelled or could not be completed.';
      setCheckoutError(errMsg);
      if (showMessage) showMessage(errMsg, 'error');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col justify-between font-sans text-slate-800">
      {/* Standalone Enterprise Navigation Bar */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-3 flex items-center justify-between shadow-2xs">
        <div className="flex items-center gap-3">
          <img
            src="/nd.png"
            alt="NEXDESK Logo"
            className="w-8 h-8 rounded-lg object-contain shadow-2xs bg-slate-50 border border-slate-100 p-0.5 flex-shrink-0"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/icon-192x192.png';
            }}
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-sm text-slate-900 tracking-tight">NEXDESK</span>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                #{clientCode}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-medium">Tenant Workspace Activation</p>
          </div>
        </div>

        {/* User Identity & Logout Button */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-extrabold flex items-center justify-center">
              {(user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="text-left">
              <div className="text-[11px] font-bold text-slate-800 leading-none">{user?.username}</div>
              <div className="text-[9px] text-slate-400 uppercase font-semibold leading-none mt-0.5">{user?.role}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
            title="Sign out or switch tenant"
          >
            <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Log Out</span>
          </button>
        </div>
      </header>

      {/* Main Activation Card */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 my-auto">
        <div className="w-full max-w-lg bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm text-center space-y-5 animate-fadeIn">
          {/* Header Icon */}
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center mx-auto shadow-2xs">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>

          {/* Heading Section */}
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200 text-[10px] font-bold uppercase tracking-wider">
              <svg className="w-3 h-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Prepaid Workspace Activation</span>
            </div>
            <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">
              Complete Payment to Access Workspace
            </h1>
            <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
              Organization <strong className="text-slate-800 font-bold">{clientName}</strong> is configured on a <strong className="text-slate-800">Prepaid</strong> subscription plan. Complete checkout to activate your isolated cloud node.
            </p>
          </div>

          {/* Error Banner */}
          {checkoutError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-left text-xs text-red-700 flex items-start gap-2 animate-fadeIn">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1 font-medium">{checkoutError}</div>
              <button onClick={() => setCheckoutError('')} className="text-red-400 hover:text-red-600 font-bold cursor-pointer">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Plan Breakdown Card */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-left space-y-3.5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Configured Package</div>
                <div className="text-sm font-extrabold text-slate-900 mt-0.5">{planName}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Payable Amount</div>
                <div className="text-base font-black text-blue-600 mt-0.5">{planPrice}</div>
              </div>
            </div>

            {/* Features Grid with Professional Vector Icons */}
            <div className="grid grid-cols-2 gap-2.5 text-xs text-slate-700 font-medium">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{userLimit} User Seats</span>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{assetLimit} Hardware Assets</span>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Full IT Service Desk</span>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Instant Activation</span>
              </div>
            </div>
          </div>

          {/* Action Trigger */}
          {isAdmin ? (
            <div className="space-y-3 pt-1">
              <button
                type="button"
                onClick={handlePayAndActivate}
                disabled={isCheckingOut}
                className="w-full py-3 px-5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-2xs transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                {isCheckingOut ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Processing Payment...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span>Pay via Cashfree & Activate Workspace</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 font-medium">
                <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure 256-bit encrypted checkout powered by Cashfree Payments</span>
              </div>
            </div>
          ) : (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 text-left space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Admin Payment Required</span>
              </div>
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Your Organization Administrator must complete the initial payment to activate the workspace before team members can log in.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Corporate Footer */}
      <footer className="py-3.5 text-center text-[11px] text-slate-400 border-t border-slate-200 bg-white">
        © {new Date().getFullYear()} NEXDESK Cloud Solutions. All rights reserved.
      </footer>
    </div>
  );
};

export default PrepaidActivationPortal;
